import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  Autocomplete,
  Tooltip,
  ListItem,
  ListItemText,
  Checkbox,
  Select,
  MenuItem,
  FormControlLabel,
} from "@mui/material";
import { Colors } from "../../../../Styles/Colors";
import ShowComponents from "../Common/ShowComponent";
import { renderIconButton, renderTitle } from "../Common/DocCommonComponent";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import {
  CommonDeleteButton,
  CommonEditButton,
  DeletePopup,
} from "../../../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../../../Utility/ImagePaths";
import RestAPIService from "../../../../Utility/Services/RestAPIService";

const headers = [
  "System",
  "Part",
  "Element",
  "Finding",
  "Follow up",
  "Frequency",
  "Remarks",
  "Action",
];

const FREQUENCY_LIST = [
  "Daily",
  "Weekly",
  "Monthly",
  "Quarterly",
  "Yearly",
];

const DocSystemicExamination = ({ Title, ...props }) => {
  const [state, setState] = useState({
    // input row
    systemValue: null,
    partValue: null,
    elementValue: null,
    finding: "",
    has_followup: false,
    frequency: "",
    remarks: "",

    // masters and fetched data
    systemList: [],
    partList: [],
    elementList: [],
    examinationData: [],

    // UI
    isEdit: false,
    selectedId: null,
    postLoading: false,
    dltLineClk: false,
    deleteLineItem: null,
    removeLineData: false,
  });

  const updateState = (updates) => setState((prev) => ({ ...prev, ...updates }));

  // get all masters + existing exam data; lazy-loaded by ShowComponents
  const getApiCall = useCallback(() => {
    getExaminationData();
    getSystemData();
    getPartData();
    getElementData();
  }, [props.appointmentId, props.isFromIp, props.ipAdmissionId]);

  useEffect(() => {
    props.registerRefreshFunction("systemic_examination", getExaminationData);
  }, []);

  const getSystemData = async () => {
    try {
      const res = await RestAPIService.getAll(Serviceurls.AYURVEDA_SYSTEM_MASTER);
      if (res?.data?.status === "success") {
        const list = res.data.data?.filter(i => i.is_active) || [];
        updateState({ systemList: list });
        // auto-select if exactly one
        // if (list.length === 1) updateState({ systemValue: list[0] });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message || "Failed to load systems");
    }
  };

  const getPartData = async () => {
    try {
      const res = await RestAPIService.getAll(Serviceurls.AYURVEDA_PART_MASTER);
      if (res?.data?.status === "success") {
        const list = res.data.data?.filter(i => i.is_active) || [];
        updateState({ partList: list });
        // auto-select if single and system is either not selected or matches
        const filtered = list;
        // Cannot auto-select here globally; auto-select will occur after system filter below
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  const getElementData = async () => {
    try {
      const res = await RestAPIService.getAll(Serviceurls.AYURVEDA_ELEMENT_MASTER);
      if (res?.data?.status === "success") {
        const list = res.data.data?.filter(i => i.is_active) || [];
        updateState({ elementList: list });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  const getExaminationData = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
      const res = await RestAPIService.getAll(
        `${Serviceurls.AYURVEDA_SYSTEMIC_EXAMINATION}${params}`
      );
      if (res?.data?.status === "success") {
        updateState({ examinationData: res.data.data || [] });
      } else {
        props.errorMessage(res?.data?.message);
      }
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  // derive filtered lists based on selections
  const filteredParts = useMemo(() => {
    if (!state.systemValue) return state.partList || [];
    return (state.partList || []).filter(
      (p) => p.system_id === state.systemValue.id || p.system === state.systemValue.id
    );
  }, [state.partList, state.systemValue]);

  const filteredElements = useMemo(() => {
    if (!state.partValue) return state.elementList || [];
    return (state.elementList || []).filter(
      (e) => e.part_id === state.partValue.id || e.part === state.partValue.id
    );
  }, [state.elementList, state.partValue]);

  // auto-select if only one after filtering
  useEffect(() => {
    if (filteredParts.length === 1 && state.systemValue) updateState({ partValue: filteredParts[0] });
    // if part changed and only one element, select element
  }, [filteredParts]);

  useEffect(() => {
    if (filteredElements.length === 1 && state.systemValue && state.partValue) updateState({ elementValue: filteredElements[0] });
  }, [filteredElements]);

  const onClearHandler = () => {
    updateState({
      isEdit: false,
      selectedId: null,
      systemValue: null,
      partValue: null,
      elementValue: null,
      finding: "",
      has_followup: false,
      frequency: "",
      remarks: "",
    });
  };

  const deleteLinePop = () => {
    updateState({
      dltLineClk: false,
      deleteLineItem: null,
      removeLineData: false,
    });
  };

  const deleteLineItem = async () => {
    try {
      updateState({ removeLineData: true });
      // delete endpoint assumed like /endpoint/{id}/
      await RestAPIService.delete(`${Serviceurls.AYURVEDA_SYSTEMIC_EXAMINATION || Serviceurls.AYURVEDA_PAST_COMPLAINTS}${state.deleteLineItem?.id}/`);
      props.successMessage("Deleted successfully");
      deleteLinePop();
      getExaminationData();
    } catch (e) {
      updateState({ removeLineData: false });
      props.errorMessage(e?.response?.data?.message || e.message);
    }
  };

  const postExamination = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const data = {
        id: state.selectedId ? state.selectedId : null,
        system: state.systemValue ? state.systemValue.id : null,
        part: state.partValue ? state.partValue.id : null,
        element: state.elementValue ? state.elementValue.id : null,
        finding: state.finding || "",
        has_followup: state.has_followup ? true : false,
        frequency: state.has_followup ? state.frequency || "" : "",
        remarks: state.remarks || "",
      };
      if (isFromIp) {
        data.ip_admission_id = ipAdmissionId;
        data.clinic_id = clinicId;
        data.doctor_id = doctorId;
      } else {
        data.appointment_id = appointmentId;
      }

      const params = Serviceurls.AYURVEDA_SYSTEMIC_EXAMINATION || Serviceurls.AYURVEDA_PAST_COMPLAINTS;
      updateState({ postLoading: true });

      const apiCall = state.selectedId
        ? RestAPIService.update(state.selectedId, data, params)
        : RestAPIService.create(data, params);

      const res = await apiCall;
      if (res?.data?.status === "success") {
        props.successMessage(res.data.message || "Saved successfully");
        onClearHandler();
        getExaminationData();
      } else {
        props.errorMessage(res?.data?.message || "Save failed");
      }
      updateState({ postLoading: false });
    } catch (e) {
      updateState({ postLoading: false });
      props.errorMessage(e?.response?.data?.message || e.message);
    }
  };

  // edit click populates top row
  const onEdit = (item) => {
    updateState({
      isEdit: true,
      selectedId: item.id,
      systemValue: item.system_details || item.system || null,
      partValue: item.part_details || item.part || null,
      elementValue: item.element_details || item.element || null,
      finding: item.finding || "",
      has_followup: !!item.has_followup,
      frequency: item.frequency || "",
      remarks: item.remarks || "",
    });
  };

  const disableBtn = useMemo(() => {
    // require at least element or finding to add; change as per business rule
    return (
      !(state.systemValue && state.partValue && state.elementValue && state.finding) ||
      props.isLocked
    );
  }, [state.systemValue, state.partValue, state.elementValue, state.finding, props.isLocked]);

  // render helpers (autocomplete and textfield)
  const renderAutoComplete = (label, stateKey, list, showKey, disabled = false) => {
    const selectedValue = state[stateKey] || null;
    return (
      <Autocomplete
        size="small"
        options={list || []}
        getOptionLabel={(option) => (typeof option === "string" ? option : option[showKey] || "")}
        value={selectedValue}
        disabled={disabled}
        onChange={(e, newVal) => {
          updateState({ [stateKey]: newVal });
          // if system changed, clear downstream
          if (stateKey === "systemValue") {
            updateState({ partValue: null, elementValue: null });
          }
          if (stateKey === "partValue") {
            updateState({ elementValue: null });
          }
        }}
        renderOption={(propsOpt, option) => (
          <li {...propsOpt} style={{ padding: "0vh 1.5vh" }}>
            <ListItem sx={{ display: "flex", justifyContent: "space-between", p: 0, m: 0 }}>
              <ListItemText
                primary={
                  option[showKey]?.length > 30 ? (
                    <Tooltip title={option[showKey]} placement="top">
                      <div>{option[showKey]?.slice(0, 21) + "..."}</div>
                    </Tooltip>
                  ) : (
                    option[showKey]
                  )
                }
              />
            </ListItem>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} size="small" label={label} autoComplete="off" />
        )}
        sx={{ width: "100%" }}
      />
    );
  };

  const renderTextField = (label, stateKey, maxLength) => (
    <TextField
      size="small"
      autoComplete="off"
      label={label}
      value={state[stateKey] || ""}
      inputProps={{ maxLength }}
      onChange={(e) => updateState({ [stateKey]: e.target.value })}
      sx={{ width: "100%" }}
    />
  );

  return (
    <ShowComponents onIntersection={getApiCall}>
      <Card
        sx={{
          width: "100%",
          height: "fit-content",
          padding: "0.3vw",
          marginBottom: "0.5vw",
          border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`,
        }}
      >
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"space-between"} padding={"0.5vw"} borderBottom={`1px solid #E3E2E2`}>
          {renderTitle(Title, props.isSelected)}
        </Box>

        <Table size="small" stickyHeader aria-label="systemic table" sx={{ maxHeight: "28vw", overflowY: "auto" }}>
          <TableHead>
            <TableRow>
              {headers.map((h, idx) => (
                <TableCell
                  key={idx}
                  align={idx === headers.length - 1 ? "center" : "left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9vw",
                    color: Colors.SecondaryText,
                    backgroundColor: Colors.Background,
                    px: "0.2vw",
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* INPUT ROW */}
            <TableRow sx={{ "& td": { px: "0.2vw", verticalAlign: "middle" } }}>
              <TableCell sx={{ width: "12vw" }}>
                {renderAutoComplete("System *", "systemValue", state.systemList, "system_name")}
              </TableCell>

              <TableCell sx={{ width: "12vw" }}>
                {renderAutoComplete("Part *", "partValue", filteredParts, "part_name", !state.systemValue)}
              </TableCell>

              <TableCell sx={{ width: "12vw" }}>
                {renderAutoComplete("Element *", "elementValue", filteredElements, "element_name", !state.partValue)}
              </TableCell>

              <TableCell sx={{ width: "14vw" }}>{renderTextField("Finding *", "finding", 250)}</TableCell>

              <TableCell sx={{ width: "6vw", textAlign: "end" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!state.has_followup}
                      onChange={(e) => updateState({ has_followup: e.target.checked, frequency: e.target.checked ? state.frequency : "" })}
                      size="small"
                    />
                  }
                  label=""
                />
              </TableCell>

              <TableCell sx={{ width: "10vw" }}>
                <Select
                  size="small"
                  fullWidth
                  value={state.frequency || ""}
                  onChange={(e) => updateState({ frequency: e.target.value })}
                  disabled={!state.has_followup}
                >
                  <MenuItem value="">Select</MenuItem>
                  {FREQUENCY_LIST.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>

              <TableCell sx={{ width: "16vw" }}>{renderTextField("Remarks", "remarks", 250)}</TableCell>

              <TableCell align="center" sx={{ width: "1vw" }}>
                {renderIconButton(
                  state.isEdit ? "Update" : "Add",
                  disableBtn ? ImagePaths.DisabledAddIcon.default : ImagePaths.AddIcon.default,
                  postExamination,
                  disableBtn,
                  "2vw"
                )}
              </TableCell>
            </TableRow>

            {/* EXISTING ROWS */}
            {state.examinationData?.length > 0 ? (
              state.examinationData.map((item, idx) => (
                <TableRow key={item.id || idx} sx={{ "& td": { pl: "0.4vw", pr: "0.7vw", fontSize: "0.9vw" } }}>
                  <TableCell>
                    {item?.system_details?.system_name || item?.system_name || "-"}
                  </TableCell>
                  <TableCell>
                    {item?.part_details?.part_name || item?.part_name || "-"}
                  </TableCell>
                  <TableCell>
                    {item?.element_details?.element_name || item?.element_name || "-"}
                  </TableCell>
                  <TableCell>
                    {item?.finding ? (item.finding.length > 30 ? (
                      <Tooltip placement="top" title={item.finding}>
                        <div>{item.finding.slice(0, 28) + "..."}</div>
                      </Tooltip>
                    ) : item.finding) : "-"}
                  </TableCell>
                  <TableCell style={{textAlign:"center"}}>
                    <Checkbox checked={!!item.has_followup} disabled size="small" />
                  </TableCell>
                  <TableCell>{item.frequency || "-"}</TableCell>
                  <TableCell>
                    {item?.remarks ? (item.remarks.length > 30 ? (
                      <Tooltip placement="top" title={item.remarks}>
                        <div>{item.remarks.slice(0, 28) + "..."}</div>
                      </Tooltip>
                    ) : item.remarks) : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Box component={"div"} display={"flex"}>
                      <CommonEditButton
                        disable={state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() => onEdit(item)}
                      />
                      <CommonDeleteButton
                        disable={state.isEdit || state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() => updateState({ dltLineClk: true, deleteLineItem: item })}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  No Records To Be Shown
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {state.dltLineClk && (
          <DeletePopup
            DeletTitle={`Are you sure you want to delete ?`}
            deleteAlertPopupClose={deleteLinePop}
            removeData={deleteLineItem}
            disable={state.removeLineData}
          />
        )}
      </Card>
    </ShowComponents>
  );
};

export default DocSystemicExamination;
